/**
 * Phản hồi 👍/👎 (Story 5.6 apus-ami-agent, FR-120): 👎 mang `category` ∈ bốn mã cố định + ghi chú tuỳ chọn.
 * Bốn mã là của ami-agent (`FeedbackService.CATEGORIES`) — đổi ở đây phải đổi ở đó, và ngược lại.
 * Thuần (không DOM) để test bằng `node --test`.
 */
export const FEEDBACK_CATEGORIES = [
  ["thieu_thong_tin", "Thiếu thông tin"],
  ["khong_trich_nguon", "Không trích nguồn"],
  ["hieu_sai_y", "Hiểu sai ý"],
  ["khac", "Khác"],
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number][0];

export const FEEDBACK_NOTE_MAX = 400;

/** O-10: người dùng phải biết trước khi gửi rằng trích đoạn có thể được quản trị xem. */
export const FEEDBACK_NOTICE = "Câu hỏi và trích đoạn câu trả lời (tối đa 300 ký tự) có thể được quản trị viên xem để cải thiện Apus Ami.";

export function isFeedbackCategory(v: unknown): v is FeedbackCategory {
  return typeof v === "string" && FEEDBACK_CATEGORIES.some(([code]) => code === v);
}

export interface FeedbackInput {
  value: "up" | "down";
  conversationId?: string | null;
  question: string;
  answer: string;
  category?: string;
  note?: string;
}

/** Thân `POST /feedback`. 👎 BẮT BUỘC có lý do hợp lệ; 👍 ⛔ mang lý do (BE trả 400 `invalid_category`). */
export function feedbackBody(input: FeedbackInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    value: input.value,
    conversationId: input.conversationId,
    question: input.question,
    answer: input.answer.slice(0, 300),
  };
  if (input.value === "down") {
    if (!isFeedbackCategory(input.category)) throw new Error("Hãy chọn lý do câu trả lời chưa tốt");
    body.category = input.category;
    const note = (input.note || "").trim().slice(0, FEEDBACK_NOTE_MAX);
    if (note) body.note = note;
  } else if (input.category !== undefined || input.note) {
    throw new Error("Chỉ phản hồi 👎 mới có lý do");
  }
  return body;
}

/**
 * Trạng thái của màn chọn lý do 👎 (Story 5.6), tách khỏi DOM để test được: chọn đúng MỘT mã, nút Gửi chỉ mở khi đã chọn,
 * ghi chú rỗng không gửi, ghi chú quá dài bị cắt ở 400. View chỉ vẽ lại theo trạng thái này.
 */
export interface FeedbackDraft {
  readonly category: FeedbackCategory | null;
  select(code: unknown): void;
  canSend(): boolean;
  /** `{category, note?}` cho `onTool`, hoặc null khi chưa chọn lý do (không có gì để gửi). */
  payload(note?: string): { category: FeedbackCategory; note?: string } | null;
}

export function createFeedbackDraft(): FeedbackDraft {
  let category: FeedbackCategory | null = null;
  return {
    get category() {
      return category;
    },
    select(code) {
      if (!isFeedbackCategory(code)) throw new Error("Lý do không hợp lệ");
      category = code;
    },
    canSend: () => category !== null,
    payload(note = "") {
      if (category === null) return null;
      const n = note.trim().slice(0, FEEDBACK_NOTE_MAX);
      return n ? { category, note: n } : { category };
    },
  };
}
